import { onMounted, onUnmounted } from 'vue';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { getGeneratingReplayIds } from '@/composables/useReplayGenerating';
import { syncGeneratingReplays } from '@/composables/useReplayPoll';
import { isAuthenticated } from '@/services/api';

/**
 * When the app returns to the foreground, sync replay generation state with the server
 * so download controls unlock and ready replays are detected without re-enqueueing.
 */
export function useReplayResume(): void {
  let appStateListener: { remove: () => Promise<void> } | null = null;

  async function onResume(): Promise<void> {
    if (!isAuthenticated()) return;
    const ids = getGeneratingReplayIds();
    if (ids.length === 0) return;
    await syncGeneratingReplays(ids);
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      void onResume();
    }
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void onResume();
      }).then((handle) => {
        appStateListener = handle;
      });
    }
  });

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    void appStateListener?.remove();
    appStateListener = null;
  });
}
